import React, { useState } from 'react';
import { downloadFile } from '../../utils/fileUtils';
import {
    Box,
    Typography,
    Alert,
    AlertTitle,
    Button,
    List,
    ListItem,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Download as DownloadIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import FieldValueMappingModal from '../modals/fieldValueMappingModal';

interface ErrorDisplayProps {
    error: {
        type: string;
        message: string;
        details: string | string[] | Record<string, string>;
    } | null;
    maxVisibleErrors?: number;
    showDownloadButton?: boolean;
    csvDownloadData?: {
        filename: string;
        data: Blob;
    } | null;
    loanType?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ 
    error, 
    maxVisibleErrors = 4,
    showDownloadButton = true,
    csvDownloadData = null,
    loanType,
}) => {
    const [expanded, setExpanded] = useState<boolean>(false);
    const [scrollTop, setScrollTop] = useState<number>(0);
    const [showMappingModal, setShowMappingModal] = useState<boolean>(false);

    if (!error) return null;

    const formatDetails = (details: string | string[] | Record<string, string>) => {
        if (typeof details === 'string') {
            return [details];
        } else if (Array.isArray(details)) {
            return details;
        } else if (typeof details === 'object' && details !== null) {
            return Object.entries(details).map(([key, value]) => `${key}: ${value}`);
        }
        return [];
    };



    const errorDetails = formatDetails(error.details);
    const hasMultipleErrors = errorDetails.length > maxVisibleErrors;
    const visibleErrors = hasMultipleErrors ? errorDetails.slice(0, maxVisibleErrors) : errorDetails;
    const hiddenErrorsCount = hasMultipleErrors ? errorDetails.length - maxVisibleErrors : 0;
    const displayErrors = expanded ? errorDetails : visibleErrors;

    const handleDownloadErrors = () => {
        if (csvDownloadData) {
            downloadFile(csvDownloadData.data, csvDownloadData.filename);
        } else {
            const csvContent = errorDetails.length > 1
                ? [
                    '#,Error Details',
                    ...errorDetails.map((detail, index) => `${index + 1},"${detail}"`)
                ].join('\n')
                : [
                    'Error Type,Message,Details',
                    `${error.type},"${error.message}","${errorDetails.join('; ')}"`
                ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const filename = `upload_errors_${new Date().toISOString().split('T')[0]}.csv`;
            downloadFile(blob, filename);
        }
    };


    // Check if we have unmapped loan types or dpd buckets
    const hasValueMappingErrors = error?.details && typeof error.details === 'object' && 
        (('Unmapped Loan Types' in error.details) || ('Unmapped DPD Buckets' in error.details));

    const getSeverityColor = (type: string) => {
        const typeLower = type.toLowerCase();
        if (typeLower === 'validation') return 'warning';
        if (['servererror', 'server_error', 'fileerror', 'file_error'].includes(typeLower)) return 'error';
        return 'error';
    };

    const headerCellStyle = {
        fontWeight: 'bold',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        opacity: Math.max(0.3, 1 - scrollTop / 100),
        transition: 'opacity 0.2s'
    };

    const DownloadButton = ({ label }: { label: string }) => (
        <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadErrors}
        >
            {label}
        </Button>
    );

    const renderErrorContent = () => {
        if (errorDetails.length <= 1) {
            return (
                <List dense sx={{ py: 0 }}>
                    {visibleErrors.map((detail, index) => (
                        <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                            <ListItemText
                                primary={detail}
                                sx={{
                                    '& .MuiListItemText-primary': {
                                        fontSize: '0.875rem',
                                        color: 'inherit'
                                    }
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            );
        }

        return (
            <TableContainer 
                component={Paper} 
                sx={{ maxHeight: 200, overflow: 'auto' }}
                onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>#</TableCell>
                            <TableCell sx={headerCellStyle}>Error Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayErrors.map((detail, index) => (
                            <TableRow key={index} hover>
                                <TableCell sx={{ width: '50px', fontWeight: 'bold' }}>
                                    {index + 1}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                    {detail}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const renderActionButtons = () => {
        if (!showDownloadButton) return null;

        const downloadLabel = csvDownloadData 
            ? `Download ${csvDownloadData.filename.replace(".csv", "")}`
            : 'Download Error Report';

        if (hasMultipleErrors) {
            return (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setExpanded(!expanded)}
                        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                        {expanded ? 'Show Less' : 'Show All Errors'}
                    </Button>
                    <DownloadButton label={csvDownloadData ? `Download ${csvDownloadData.filename.replace(".csv", "")}` : 'Download CSV'} />
                    {hasValueMappingErrors && (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SettingsIcon />}
                            onClick={() => setShowMappingModal(true)}
                        >
                            Configure Field Value Mappings
                        </Button>
                    )}
                </Box>
            );
        }

        if (errorDetails.length > 1 || csvDownloadData || hasValueMappingErrors) {
            return (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {(errorDetails.length > 1 || csvDownloadData) && (
                        <DownloadButton label={downloadLabel} />
                    )}
                    {hasValueMappingErrors && (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SettingsIcon />}
                            onClick={() => setShowMappingModal(true)}
                        >
                            Configure Field Value Mappings
                        </Button>
                    )}
                </Box>
            );
        }

        return null;
    };

    return (
        <>
            <Alert 
                severity={getSeverityColor(error.type) as any}
                sx={{ mb: 2, maxWidth: '100%' }}
            >
                <AlertTitle>
                    {error.type} Error: {error.message}
                </AlertTitle>

                {errorDetails.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        {renderErrorContent()}

                        {hasMultipleErrors && !expanded && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                ... and {hiddenErrorsCount} more errors
                            </Typography>
                        )}
                        
                        {renderActionButtons()}
                    </Box>
                )}
            </Alert>
            
            {/* Field Value Mapping Modal */}
            {showMappingModal && loanType && (
                <FieldValueMappingModal
                    open={showMappingModal}
                    onClose={()=> setShowMappingModal(false)}
                    loanType={loanType}
                />
            )}
        </>
    );
};

export default ErrorDisplay; 